/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';

/**
 * @hook useHasMounted
 *
 * Provides a utility hook which is to be used when a component will only be rendered
 * based on some state. For example, an Editor should generally only be rendered when
 * a user selects an item for editing, etc. In such cases, the component's internal
 * state will be such that it will be displayed and removed immediately based on the
 * conditional state which dictates whether or not it should be displayed.
 *
 * This hook allows for retaining animations for such use cases as stated above.
 *
 * ```typescript
 *
 *   import {useAnimatedMount} from '@ion/dls/src/hooks/useAnimatedMount';
 *
 *   const {isVisible, onRemove, timeout} = useAnimatedMount();
 *
 *   const onSave = function(): void {
 *     onRemove(function() {
 *       onSave(updates);
 *     });
 *   };
 *
 *   const onClose = function(): void {
 *     onRemove(onClose);
 *   };
 *
 *   ...
 *
 *   return (
 *     <Dialog open={isVisible} transitionDuration={timeout} ...>
 *       ...
 *     </Dialog>
 *   );
 *
 * ```
 */
export function useAnimatedMount(timeout = 300) {
  const isMounted = useHasMounted();
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    if (isMounted) {
      setIsVisible(true);
    }
  }, [isMounted]);

  /**
   * Invoked when the user chooses to close the column chooser without saving their
   * selections.
   */
  const onRemove = function(callback: Function, event?: any): void {
    setIsVisible(false);
    const id = setTimeout(() => {
      clearTimeout(id);
      callback(event);
    }, timeout);
  };

  return {
    isVisible,
    onRemove,
    timeout
  };
};

export default useAnimatedMount;
